/**
 * The model in ISO weeks (Monday to Sunday, as the Daily Report keys them),
 * built from the same daily plan the Daily view draws, against actuals.
 *
 * The week in progress is judged against the part of each plan that falls on
 * the days already gone, today included for the part of it that has passed.
 * Next week's ramped plan gets its own card: the weekly answer to "what
 * should we spend".
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui'
import ModelChart from './ModelChart'
import { Tile, Notice } from './parts'
import { scienceModelAPI } from '../../services/api'
import { weeklySeries, addDays, mondayOf, DAILY_METRICS } from '../../lib/scienceModelDaily'
import { FORMATTERS, formatVariance, variance } from '../../lib/scienceModelView'

const METRICS = ['newCustomers', 'revenue', 'spend']

/** Weeks shown past the current one, unless the whole horizon is asked for. */
const WEEKS_AHEAD = 8

function todayEt() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date())
}

/** "Sep 14" */
function weekLabel(monday) {
  return new Date(`${monday}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export default function WeeklyView({ startMonth, daily, ramped }) {
  const [actual, setActual] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [wholeHorizon, setWholeHorizon] = useState(false)

  const startDate = `${startMonth}-01`
  const fetchDays = useCallback(async () => {
    const today = todayEt()
    if (today < startDate) {
      setActual({ asOf: today, today_fraction: 0, days: [], startDate })
      return
    }
    try {
      setLoading(true)
      setError(null)
      const response = await scienceModelAPI.getDayActuals({ startDate, endDate: today })
      if (response.data.success) setActual(response.data.data)
    } catch (err) {
      console.error('[SCIENCE-MODEL] Weekly error:', err)
      setError(err.response?.data?.error?.message || err.message)
      setActual(null)
    } finally {
      setLoading(false)
    }
  }, [startDate])

  useEffect(() => { fetchDays() }, [fetchDays])

  // A response for another start month is stale.
  const usable = actual?.startDate === startDate ? actual : null

  const series = useMemo(() => Object.fromEntries(
    METRICS.map(metric => [metric, weeklySeries(daily, ramped?.daily || null, usable, metric)])
  ), [daily, ramped, usable])

  const asOf = usable?.asOf || todayEt()
  const currentWeek = mondayOf(asOf)
  const nextWeek = addDays(currentWeek, 7)
  const lastShown = wholeHorizon ? null : addDays(currentWeek, 7 * WEEKS_AHEAD)
  const visible = (weeks) => weeks.filter(w => lastShown == null || w.week <= lastShown)

  const thisWeek = Object.fromEntries(METRICS.map(m => [m, series[m].find(w => w.week === currentWeek) || null]))
  const next = Object.fromEntries(METRICS.map(m => [m, series[m].find(w => w.week === nextWeek) || null]))
  const weekStarted = thisWeek.spend?.daysHappened > 0

  const chartData = (metric) => visible(series[metric]).map(w => ({
    date: w.week,
    label: weekLabel(w.week),
    plan: w.plan,
    ramped: w.ramped,
    actual: w.actual,
    partial: w.partial,
  }))

  const tileFor = (metric, title, format) => {
    const week = thisWeek[metric]
    if (!week) return <Tile key={metric} title={title} format={format} value={null} plan={null} note="This week is outside the model" />
    return (
      <Tile
        key={metric}
        title={title} format={format} planLabel="Plan to date"
        value={weekStarted ? week.actual : null} plan={week.planToDate}
        delta={weekStarted ? variance(week.actual, week.planToDate) : null}
        ramped={week.rampedToDate} rampedLabel="Ramped to date"
        rampedDelta={weekStarted && week.rampedToDate != null ? variance(week.actual, week.rampedToDate) : null}
        note={`Week of ${weekLabel(currentWeek)} · full week plan ${FORMATTERS[format].full(week.plan)}${week.ramped != null ? `, ramped ${FORMATTERS[format].full(week.ramped)}` : ''}`}
      />
    )
  }

  const cac = (spend, customers) => (spend != null && customers > 0 ? spend / customers : null)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="checkbox" checked={wholeHorizon} onChange={e => setWholeHorizon(e.target.checked)} className="accent-primary" />
          Show the whole 15-month horizon (otherwise through {WEEKS_AHEAD} weeks ahead)
        </label>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {error && <Notice tone="error">Actuals could not be loaded: {error}. The weekly plan is shown on its own.</Notice>}
      {usable?.ad_spend_available === false && (
        <Notice>No ad account has reported spend for Science.co, so actual spend and CAC read as zero.</Notice>
      )}

      {ramped && next.spend?.ramped != null && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Next week · {weekLabel(nextWeek)}</CardTitle>
            <CardDescription className="text-xs">The ramped plan for next week, with the sheet&apos;s plan for scale.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Ad spend', metric: 'spend', format: 'money' },
              { label: 'New customers', metric: 'newCustomers', format: 'count' },
              { label: 'Revenue', metric: 'revenue', format: 'money' },
            ].map(({ label, metric, format }) => (
              <div key={metric} className="space-y-0.5">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-xl font-semibold tabular-nums">{FORMATTERS[format].full(next[metric]?.ramped)}</div>
                <div className="text-xs text-muted-foreground tabular-nums">Sheet plan {FORMATTERS[format].full(next[metric]?.plan)}</div>
              </div>
            ))}
            <div className="space-y-0.5">
              <div className="text-xs text-muted-foreground">CAC at ramped plan</div>
              <div className="text-xl font-semibold tabular-nums">{FORMATTERS.money.full(cac(next.spend?.ramped, next.newCustomers?.ramped))}</div>
              <div className="text-xs text-muted-foreground">Ad spend ÷ new customers</div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        {tileFor('newCustomers', 'New customers this week', 'count')}
        {tileFor('revenue', 'Revenue this week', 'money')}
        {tileFor('spend', 'Ad spend this week', 'money')}
        <Tile
          title="CAC this week" invert
          value={weekStarted ? cac(thisWeek.spend?.actual, thisWeek.newCustomers?.actual) : null}
          plan={cac(thisWeek.spend?.plan, thisWeek.newCustomers?.plan)}
          delta={weekStarted ? variance(cac(thisWeek.spend?.actual, thisWeek.newCustomers?.actual), cac(thisWeek.spend?.plan, thisWeek.newCustomers?.plan)) : null}
          note={`Week of ${weekLabel(currentWeek)}, to date`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <ModelChart title="New customers per week" format="count" height={240} data={chartData('newCustomers')} showReforecast={false} labelPrefix="Week of " />
        <ModelChart title="Revenue per week" height={240} data={chartData('revenue')} showReforecast={false} labelPrefix="Week of " />
        <ModelChart title="Ad spend per week" height={240} data={chartData('spend')} showReforecast={false} labelPrefix="Week of " />
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs tabular-nums">
            <thead>
              <tr className="border-b">
                <th rowSpan={2} className="sticky left-0 bg-card px-3 py-2 text-left font-medium text-muted-foreground">Week of</th>
                {METRICS.map(metric => (
                  <th key={metric} colSpan={ramped ? 4 : 3} className="px-2 pt-2 text-center font-semibold border-l">{DAILY_METRICS[metric].label}</th>
                ))}
              </tr>
              <tr className="border-b">
                {METRICS.map(metric => (ramped ? ['Plan', 'Ramped', 'Actual', 'vs ramped'] : ['Plan', 'Actual', 'vs plan']).map((h, i) => (
                  <th key={`${metric}-${h}`} className={`px-2 pb-2 text-right font-medium text-muted-foreground whitespace-nowrap ${i === 0 ? 'border-l' : ''}`}>{h}</th>
                )))}
              </tr>
            </thead>
            <tbody>
              {visible(series.spend).map((week, i) => (
                <tr key={week.week} className={`border-b last:border-0 ${week.week === currentWeek ? 'bg-muted/40' : ''}`}>
                  <td className="sticky left-0 bg-card px-3 py-1.5 whitespace-nowrap">
                    {weekLabel(week.week)}
                    {week.days < 7 && <span className="ml-1 text-muted-foreground">({week.days} days in model)</span>}
                    {week.partial && <span className="ml-1 text-muted-foreground">(to date)</span>}
                  </td>
                  {METRICS.map(metric => {
                    const full = FORMATTERS[DAILY_METRICS[metric].format].full
                    const w = visible(series[metric])[i]
                    // The week in progress is compared with what should have happened by now.
                    const base = ramped
                      ? (w.partial ? w.rampedToDate : w.ramped)
                      : (w.partial ? w.planToDate : w.plan)
                    return [
                      <td key="p" className="px-2 py-1.5 text-right border-l">{full(w.plan)}</td>,
                      ramped && <td key="r" className="px-2 py-1.5 text-right">{full(w.ramped)}</td>,
                      <td key="a" className="px-2 py-1.5 text-right">{w.actual != null ? full(w.actual) : ''}</td>,
                      <td key="v" className="px-2 py-1.5 text-right">{w.actual != null ? formatVariance(variance(w.actual, base)) : ''}</td>,
                    ]
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  )
}
